/**
 * Dashboard Page
 * Overview of posts, analytics, and brand selector
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBrand } from '../contexts/BrandContext';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../lib/api';
import { Card, CardHeader } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Select } from '../components/ui/Select';
import { Icon, type IconName } from '../components/icons';
import type { Brand, Post } from '../types';

export function Dashboard() {
  const { selectedBrand, setSelectedBrand } = useBrand();
  const { user } = useAuth();
  const [brands, setBrands] = useState<Brand[]>([]);
  const [recentPosts, setRecentPosts] = useState<Post[]>([]);
  const [stats, setStats] = useState({
    total_posts: 0,
    scheduled: 0,
    published: 0,
    drafts: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  // Fetch brands on mount
  useEffect(() => {
    const fetchBrands = async () => {
      try {
        const response = await apiClient.getBrands();
        if (response.success && response.data) {
          const brandsData = response.data as Brand[];
          setBrands(brandsData);

          // Restore previously selected brand or fall back to first
          if (!selectedBrand && brandsData.length > 0) {
            const savedBrandId = localStorage.getItem('selected_brand_id');
            const savedBrand = savedBrandId
              ? brandsData.find((b) => b.id === savedBrandId)
              : null;
            setSelectedBrand(savedBrand || brandsData[0]);
          }
        }
      } catch (error) {
        console.error('Failed to fetch brands:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBrands();
  }, []);

  // Fetch posts when brand changes
  useEffect(() => {
    if (selectedBrand) {
      fetchPosts();
    }
  }, [selectedBrand]);

  const fetchPosts = async () => {
    if (!selectedBrand) return;

    try {
      const response = await apiClient.getPosts({
        brand_id: selectedBrand.id,
        limit: 5,
      });

      if (response.success && response.data) {
        setRecentPosts(response.data);

        // Calculate stats (in real app, this would come from API)
        const total = response.pagination?.total || 0;
        setStats({
          total_posts: total,
          scheduled: response.data.filter((p) => p.status === 'scheduled').length,
          published: response.data.filter((p) => p.status === 'published').length,
          drafts: response.data.filter((p) => p.status === 'draft').length,
        });
      }
    } catch (error) {
      console.error('Failed to fetch posts:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  if (brands.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Card className="text-center">
          <p className="text-gray-500">No brands found</p>
        </Card>
      </div>
    );
  }

  const brandOptions = brands.map((brand) => ({
    value: brand.id,
    label: brand.name,
  }));

  return (
    <div className="space-y-6">
      {/* Hero Section with Logo */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-2xl p-8 text-white shadow-xl">
        <div className="flex flex-col items-center text-center">
          <img
            src="/images/logo_with_k.png"
            alt="Jonkai Studio"
            className="w-32 h-32 sm:w-40 sm:h-40 object-contain mb-4"
          />
          <p className="text-blue-100 text-lg max-w-xl">
            AI-Powered Brand Management & Content Publishing
          </p>
        </div>
      </div>

      {/* Verification banner */}
      {user?.is_verified === false && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-lg p-4 flex items-start gap-3">
          <Icon name="alert" size="lg" className="mt-0.5" />
          <div>
            <p className="font-semibold">Verify your email</p>
            <p className="text-sm text-amber-700">
              We sent a verification link when you signed up. Resend it from Settings if needed.
            </p>
          </div>
        </div>
      )}

      {/* Work in Progress Banner */}
      <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg p-4 shadow-lg">
        <div className="flex items-center gap-3">
          <Icon name="alert" size="lg" className="flex-shrink-0" />
          <div>
            <p className="font-semibold">Work in Progress</p>
            <p className="text-sm opacity-90">
              Jonkai Studio is currently under development. Some features may not be fully functional yet. Stay tuned for updates!
            </p>
          </div>
        </div>
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600 mt-1 text-sm sm:text-base">
            Manage your social media content across all platforms
          </p>
        </div>
        <Button onClick={() => navigate('/posts/new')} className="w-full sm:w-auto">
          <Icon name="edit" size="sm" className="mr-2" />
          Create Post
        </Button>
      </div>

      {/* Brand Selector */}
      <Card>
        <div className="max-w-xs">
          <Select
            label="Select Brand"
            options={brandOptions}
            value={selectedBrand?.id || ''}
            onChange={(e) => {
              const brand = brands.find((b) => b.id === e.target.value);
              if (brand) setSelectedBrand(brand);
            }}
          />
        </div>
      </Card>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          icon="file-text"
          label="Total Posts"
          value={stats.total_posts}
          color="blue"
        />
        <StatCard
          icon="calendar"
          label="Scheduled"
          value={stats.scheduled}
          color="purple"
        />
        <StatCard
          icon="trending-up"
          label="Published"
          value={stats.published}
          color="green"
        />
        <StatCard
          icon="edit"
          label="Drafts"
          value={stats.drafts}
          color="gray"
        />
      </div>

      {/* Recent Posts */}
      <Card>
        <CardHeader
          title="Recent Posts"
          subtitle={`Latest posts for ${selectedBrand?.name}`}
          action={
            <Button variant="outline" size="sm" onClick={() => navigate('/posts')}>
              View All
            </Button>
          }
        />

        {recentPosts.length === 0 ? (
          <div className="text-center py-12">
            <Icon name="edit" size="3xl" className="mx-auto mb-4 opacity-40" />
            <p className="text-gray-500">No posts yet</p>
            <Button
              variant="outline"
              size="sm"
              className="mt-4"
              onClick={() => navigate('/posts/new')}
            >
              Create your first post
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {recentPosts.map((post) => (
              <div
                key={post.id}
                className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
                onClick={() => navigate(`/posts/${post.id}`)}
              >
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900">
                    {post.title || 'Untitled Post'}
                  </h4>
                  <p className="text-sm text-gray-500 mt-1 line-clamp-1">
                    {post.content}
                  </p>
                  <div className="flex items-center gap-2 mt-2">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        post.status === 'published'
                          ? 'bg-green-100 text-green-800'
                          : post.status === 'scheduled'
                          ? 'bg-purple-100 text-purple-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {post.status}
                    </span>
                    <span className="text-xs text-gray-500">
                      {post.platforms.join(', ')}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

interface StatCardProps {
  icon: IconName;
  label: string;
  value: number;
  color: 'blue' | 'purple' | 'green' | 'gray';
}

function StatCard({ icon, label, value, color }: StatCardProps) {
  const colors = {
    blue: 'bg-blue-500',
    purple: 'bg-purple-500',
    green: 'bg-green-500',
    gray: 'bg-gray-500',
  };

  return (
    <Card>
      <div className="flex items-center">
        <div className={`p-3 rounded-lg ${colors[color]}`}>
          <Icon name={icon} size="lg" />
        </div>
        <div className="ml-4">
          <p className="text-sm font-medium text-gray-600">{label}</p>
          <p className="text-2xl font-semibold text-gray-900">{value}</p>
        </div>
      </div>
    </Card>
  );
}
